import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  type Identifier,
  useGetList,
  useGetRecordRepresentation,
  useTranslate,
  useUpdate,
} from "ra-core";
import { Link } from "react-router";
import { ReferenceField } from "@/components/admin/reference-field";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import {
  postponedToNextMonday,
  postponedToNextWeek,
  postponedToTomorrow,
} from "../tasks/taskPostpone";
import { TaskOwnerAvatar } from "../tasks/TaskOwnerAvatar";
import { TaskCadence, TaskDueCell, TaskTypeIcon } from "../tasks/TaskRowFields";
import { TaskRowActions } from "../tasks/TaskRowActions";
import { isOverdue, isRecentlyDone } from "../tasks/tasksPredicate";
import type { Company, Contact, Deal, Task } from "../types";
import { useDashboardScope } from "./useDashboardScope";

/**
 * A task carries its own account, so the panel reads them in a single indexed
 * query. That also picks up the tasks opened against the account itself or
 * against one of its deals, which reaching them through the contacts never
 * could. The window is capped: this is a triage list, not the /tasks page.
 */
const TASKS_LIMIT = 20;

/**
 * Where a row points. The person the task names wins, then the deal, then the
 * account it belongs to.
 */
const taskTargetPath = (company: Company, task: Task) => {
  if (task.contact_id != null) return `/contacts/${task.contact_id}/show`;
  if (task.deal_id != null) return `/deals/${task.deal_id}/show`;
  return `/companies/${company.id}/show`;
};

/**
 * The open tasks of one account, shown when its row is expanded.
 *
 * Built out of the same row parts as /tasks and My Day rather than its own
 * markup: the reader has all three on one screen, and a task that reads three
 * different ways across them is what made this panel unreadable. What it
 * deliberately does NOT inherit is the full row layout -- at half the width two
 * designed lines beat an eight-column grid, and a bulk-selection checkbox would
 * collide with the done box inside a table row.
 */
export const AccountTasks = ({ company }: { company: Company }) => {
  const translate = useTranslate();
  const queryClient = useQueryClient();
  const { isTeam } = useDashboardScope();
  const [update] = useUpdate();

  /*
    Completing a task removes it from this query, so with nowhere to hold it the
    row vanished mid-read and could not be brought back. Kept here for the same
    grace window the task list uses -- still ticked, struck through -- so a
    brushed click is recoverable on the surface it happened on.
  */
  const [justCompleted, setJustCompleted] = useState<Task[]>([]);
  const [pendingId, setPendingId] = useState<Identifier | null>(null);

  const {
    data: openTasks,
    total: totalTasks,
    isPending,
  } = useGetList<Task>("tasks", {
    pagination: { page: 1, perPage: TASKS_LIMIT },
    sort: { field: "due_date", order: "ASC" },
    filter: { company_id: company.id, "done_date@is": null },
  });

  const tasks = useMemo(() => {
    const open = openTasks ?? [];
    const openIds = new Set(open.map((task) => String(task.id)));
    const held = justCompleted.filter(
      (task) =>
        !openIds.has(String(task.id)) &&
        // The predicate models done_date as required-and-nullable while the
        // record leaves it optional. Same values, so the shape is adapted here
        // rather than the predicate re-implemented.
        isRecentlyDone({
          due_date: task.due_date,
          done_date: task.done_date ?? null,
        }),
    );
    return [...open, ...held].sort((a, b) => {
      // Undated tasks sort last: unscheduled is not urgent.
      if (a.due_date == null) return b.due_date == null ? 0 : 1;
      if (b.due_date == null) return -1;
      return a.due_date.localeCompare(b.due_date);
    });
  }, [openTasks, justCompleted]);

  const lateCount = tasks.filter(
    (task) => task.done_date == null && isOverdue(task.due_date),
  ).length;

  const write = (task: Task, data: Partial<Task>, held: Task[]) => {
    // Per task, not one flag for the panel: completing one row used to grey out
    // every checkbox until the write returned.
    setPendingId(task.id);
    update(
      "tasks",
      { id: task.id, data, previousData: task },
      {
        onSettled: () => setPendingId(null),
        onSuccess: () => {
          setJustCompleted(held);
          // The row above counts these tasks, so its company must be refetched
          // along with the list the task left.
          queryClient.invalidateQueries({ queryKey: ["tasks"] });
          queryClient.invalidateQueries({ queryKey: ["companies"] });
        },
      },
    );
  };

  const handleToggle = (task: Task) => () => {
    if (task.done_date != null) {
      write(
        task,
        { done_date: null },
        justCompleted.filter((held) => held.id !== task.id),
      );
      return;
    }
    const done_date = new Date().toISOString();
    write(task, { done_date }, [...justCompleted, { ...task, done_date }]);
  };

  const handlePostpone = (task: Task, nextDueDate: Date) => () =>
    write(task, { due_date: nextDueDate.toISOString() }, justCompleted);

  if (isPending) {
    return (
      <div className="flex flex-col gap-2 p-4">
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-6 w-full" />
      </div>
    );
  }

  if (!tasks.length) {
    return (
      <p className="p-4 text-sm text-muted-foreground">
        {translate("resources.tasks.empty")}
      </p>
    );
  }

  const isCapped = totalTasks != null && totalTasks > (openTasks?.length ?? 0);

  return (
    <div className="flex flex-col gap-2 p-4">
      {/*
        The account row above carries these counts, but it is out of the way
        once the panel is open -- and which of them is late is the reason the
        panel was opened at all.
      */}
      <p className="text-xs text-muted-foreground">
        {lateCount > 0 && (
          <span className="font-medium text-destructive">
            {translate("crm.dashboard.accounts.tasks_late", {
              smart_count: lateCount,
            })}{" "}
          </span>
        )}
        {translate("crm.dashboard.accounts.tasks_open", {
          smart_count: totalTasks ?? tasks.length,
        })}
      </p>

      <ul className="flex flex-col divide-y">
        {tasks.map((task) => {
          const isDone = task.done_date != null;

          return (
            <li
              key={task.id}
              className="group flex flex-col gap-0.5 py-1.5 text-sm transition-colors first:pt-0 last:pb-0 hover:bg-muted/50"
            >
              <div className="flex items-start gap-2">
                {/*
                  The only control that writes done_date, and it toggles both
                  ways: a task completed by a brushed click has to be
                  recoverable from the surface it disappeared from.
                */}
                <Checkbox
                  checked={isDone}
                  disabled={pendingId === task.id}
                  onCheckedChange={handleToggle(task)}
                  aria-label={task.text}
                  className="mt-0.5"
                />
                <TaskTypeIcon type={task.type} className="mt-0.5" />
                {/*
                  A link, deliberately not a <label>: a label bound to the
                  checkbox made the ordinary act of clicking a task to read it
                  complete the task, and the panel then dropped it.
                */}
                <Link
                  to={taskTargetPath(company, task)}
                  className={cn(
                    "min-w-0 flex-1 whitespace-pre-line hover:underline",
                    isDone && "text-muted-foreground line-through",
                  )}
                >
                  {task.text}
                </Link>
                <div className="shrink-0">
                  {/*
                    Postponing only. Editing opens a sheet over the dashboard
                    and deleting is too destructive to offer from a panel opened
                    to read -- both stay on /tasks.
                  */}
                  <TaskRowActions
                    onPostponeTomorrow={handlePostpone(
                      task,
                      postponedToTomorrow(task),
                    )}
                    onPostponeNextWeek={handlePostpone(
                      task,
                      postponedToNextWeek(task),
                    )}
                    onPostponeNextMonday={handlePostpone(
                      task,
                      postponedToNextMonday(task),
                    )}
                  />
                </div>
              </div>

              {/*
                One muted second line, so a wrapping row is a designed shape
                rather than an accident: everything qualifying the task sits
                here and never shares a line with the task's own text.
              */}
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 pl-8 text-xs text-muted-foreground">
                <TaskDueCell task={task} />
                <TaskCadence task={task} />
                <TaskTarget task={task} />
                {/*
                  Repetition on a single-manager dashboard -- every row would
                  carry the same name -- and information once the scope spans
                  the team.
                */}
                {isTeam && <TaskOwnerAvatar task={task} />}
              </div>
            </li>
          );
        })}
      </ul>

      {/*
        The fetch is capped, so say which slice of the account's tasks this is
        and send the rest to the account page. Deliberately not
        `ra.navigation.page_range_info`: that is a list toolbar's language, and
        this panel has no pagination to go with it.
      */}
      {isCapped && (
        <p className="text-xs text-muted-foreground">
          {translate("crm.dashboard.accounts.tasks_capped", {
            shown: openTasks?.length ?? 0,
            total: totalTasks ?? 0,
          })}{" "}
          <Link
            to={`/companies/${company.id}/show`}
            className="underline hover:text-foreground"
          >
            {translate("crm.common.load_more")}
          </Link>
        </p>
      )}
    </div>
  );
};

/**
 * What the task is about: the person when it names one, else the deal, else the
 * account itself.
 *
 * Rendered as text, not a link. The row's own text already points at the same
 * destination, so linking the name a second time offered a choice between two
 * identical routes -- and the underlined one read as the more clickable.
 */
const TaskTarget = ({ task }: { task: Task }) => {
  const translate = useTranslate();
  const getContactRepresentation = useGetRecordRepresentation("contacts");

  if (task.contact_id != null) {
    return (
      <ReferenceField<Task, Contact>
        source="contact_id"
        reference="contacts"
        record={task}
        link={false}
        render={({ referenceRecord }) =>
          referenceRecord ? (
            <span>{getContactRepresentation(referenceRecord)}</span>
          ) : null
        }
      />
    );
  }

  if (task.deal_id != null) {
    return (
      <ReferenceField<Task, Deal>
        source="deal_id"
        reference="deals"
        record={task}
        link={false}
        render={({ referenceRecord }) =>
          referenceRecord ? <span>{referenceRecord.name}</span> : null
        }
      />
    );
  }

  /*
    Inside this account's own panel the company name reads as a person's unless
    it is named for what it is. The label is the information here: the task is
    about the account rather than anyone at it.
  */
  return <span>{translate("resources.tasks.target.company")}</span>;
};
